import React, { useState, useEffect, useRef, useMemo } from "react";
import Slider from "react-slick";
import './index.css'

function CustomNextArrow(props) {
    const { onClick, setCurrentImageIndex, images } = props;
    return (
        <div
            className="slickArrow slickNext fa-solid fa-chevron-right"
            onClick={() => {
                onClick();
                setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
            }}
        />
    );
}

function CustomPrevArrow(props) {
    const { onClick, setCurrentImageIndex, images } = props;
    return (
        <div
            className="slickArrow slickPrev fa-solid fa-chevron-left"
            onClick={() => {
                onClick();
                setCurrentImageIndex((prevIndex) => (prevIndex + (images.length - 1)) % images.length);
            }}
        />
    );
}

function getImageTypeLabel(type) {
    return Number(type) === 0 ? 'Pre-Op' : 'Post-Op';
}

function ImagesSlider(props) {
    const rawImages = props.caseData?.images || [];

    const images = useMemo(() => (
        rawImages
            .map((item, index) => {
                const url = typeof item === 'string'
                    ? item
                    : (item?.url || item?.imageUrl || item?.imageData || item?.src || '');
                if (!url || typeof url !== 'string') {
                    return null;
                }
                return {
                    id: item?.id ?? index,
                    url,
                    type: item?.type ?? 1,
                    label: getImageTypeLabel(item?.type ?? 1),
                };
            })
            .filter(Boolean)
    ), [rawImages]);

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [failedUrls, setFailedUrls] = useState({});
    const [nav1, setNav1] = useState(null);
    const [nav2, setNav2] = useState(null);
    let sliderRef1 = useRef(null);
    let sliderRef2 = useRef(null);

    const visibleImages = useMemo(
        () => images.filter((img) => !failedUrls[img.url]),
        [images, failedUrls]
    );

    useEffect(() => {
        setNav1(sliderRef1.current);
        setNav2(sliderRef2.current);
    }, [visibleImages.length]);

    useEffect(() => {
        if (currentImageIndex >= visibleImages.length) {
            setCurrentImageIndex(0);
        }
    }, [visibleImages.length, currentImageIndex]);

    const markFailed = (url) => {
        setFailedUrls((prev) => (prev[url] ? prev : { ...prev, [url]: true }));
    };

    const settings = {
        infinite: visibleImages.length > 1,
        speed: 500,
        slidesToShow: (visibleImages?.length === 2) ? 2 : (visibleImages?.length == 3) ? 3 : (visibleImages?.length > 3) && 4,
        slidesToScroll: 1,
        swipeToSlide: true,
        focusOnSelect: true,
        asNavFor: nav2,
        nextArrow: <CustomNextArrow setCurrentImageIndex={setCurrentImageIndex} images={visibleImages} />,
        prevArrow: <CustomPrevArrow setCurrentImageIndex={setCurrentImageIndex} images={visibleImages} />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: (visibleImages?.length === 2) ? 2 : (visibleImages?.length > 2) && 3,
                    slidesToScroll: 1,
                    infinite: visibleImages.length > 1,
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: (visibleImages?.length > 1) && 2,
                    slidesToScroll: 1,
                    infinite: visibleImages.length > 1,
                }
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    infinite: visibleImages.length > 1,
                }
            }
        ]
    };

    const thumbSettings = {
        ...settings,
        asNavFor: nav1,
        fade: true,
        arrows: false,
        slidesToShow: 1
    };

    const active = visibleImages[currentImageIndex] || visibleImages[0];

    return (
        <div className="dbCol">
            <article className="widgetWrap sliderWidget">
                <div className="widgetHeader">
                    <span>Case Images</span>
                </div>
                <div className="widgetBody noPadding">
                    {visibleImages.length === 0 ? (
                        <div className="caseImagesEmpty">
                            <i className="fa-regular fa-image" aria-hidden="true" />
                            <p className="caseImagesEmptyTitle">No images to display</p>
                            <p className="caseImagesEmptyHint">
                                This case has no loadable images yet.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="thumbSlider posRelative">
                                <Slider
                                    ref={sliderRef1}
                                    {...thumbSettings}
                                >
                                    <div className="imgWrap posRelative">
                                        {active ? (
                                            <>
                                                <img
                                                    className="imgFluid"
                                                    src={active.url}
                                                    alt={`${active.label} case image ${currentImageIndex + 1}`}
                                                    onError={() => markFailed(active.url)}
                                                />
                                                <span className={`typeBadge ${active.label}`}>
                                                    {active.label}
                                                </span>
                                            </>
                                        ) : null}
                                    </div>
                                </Slider>
                            </div>
                            <div className="gallerySlider posRelative">
                                {visibleImages.length > 1 && (
                                    <Slider
                                        ref={sliderRef2}
                                        {...settings}
                                    >
                                        {visibleImages.map((img, index) => (
                                            <div key={img.id ?? index} className="imgWrap posRelative">
                                                <img
                                                    onClick={() => setCurrentImageIndex(index)}
                                                    className="imgFluid"
                                                    src={img.url}
                                                    alt={`${img.label} thumbnail ${index + 1}`}
                                                    onError={() => markFailed(img.url)}
                                                />
                                                <span className={`typeBadge ${img.label}`}>
                                                    {img.label}
                                                </span>
                                            </div>
                                        ))}
                                    </Slider>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </article>
        </div>
    );
}

export default ImagesSlider;
